export const enHRManagement = {
  hero: {
    title: "Web HR Management System for SMEs",
    subtitle: "A comprehensive web application for human resources management in small and medium enterprises.",
    imageAlt: "HR management system dashboard showing employee overview"
  },
  features: {
    employeeManagement: {
      title: "Employee Management",
      description: "Complete employee records with personal details, contracts, and documents management."
    },
    departmentManagement: {
      title: "Department Management",
      description: "Organize your company structure with departments and team hierarchies."
    },
    attendanceTracking: {
      title: "Attendance Tracking",
      description: "Monitor employee attendance, late arrivals, and absences with detailed reports."
    },
    payrollManagement: {
      title: "Payroll Management",
      description: "Automate salary calculations, deductions, and generate payslips."
    },
    leaveManagement: {
      title: "Leave Management",
      description: "Handle vacation requests, sick leaves, and other time-off with approval workflows."
    },
    expenseManagement: {
      title: "Expense Management",
      description: "Track and manage employee expenses with reimbursement workflows."
    }
  },
  techStack: {
    title: "Technology Stack",
    backend: {
      title: "Backend",
      php: "PHP 8.1+ for robust backend operations",
      laravel: "Laravel framework with RESTful API architecture",
      security: "Role-based authentication and authorization"
    },
    frontend: {
      title: "Frontend",
      framework: "React.js with modern UI components",
      typescript: "TypeScript for type safety",
      responsive: "Fully responsive design system"
    },
    integrations: {
      title: "Integrations",
      payroll: "Payroll service integrations",
      calendar: "Calendar system synchronization",
      analytics: "HR analytics and reporting tools"
    }
  },
  dashboard: {
    title: "Powerful HR Dashboard",
    description: "Complete control over your human resources operations with our comprehensive admin panel.",
    demoAlt: "HR management platform admin dashboard preview",
    features: {
      "1": "Comprehensive employee data management",
      "2": "Real-time attendance monitoring",
      "3": "Payroll processing and reporting",
      "4": "Customizable access permissions",
      "5": "Workforce analytics and insights"
    }
  },
  cta: {
    title: "Ready to Streamline Your HR Processes?",
    description: "Discover how our HR Management System can transform your business operations.",
    buttonText: "View Demo",
    buttonAriaLabel: "View demo of the HR management platform"
  }
};


export const frHRManagement = {
  hero: {
    title: "Système de Gestion des Ressources Humaines Web pour PME",
    subtitle: "Une application web complète pour la gestion des ressources humaines dans les petites et moyennes entreprises.",
    imageAlt: "Tableau de bord du système de gestion des ressources humaines montrant un aperçu des employés"
  },
  features: {
    employeeManagement: {
      title: "Gestion des Employés",
      description: "Dossiers complets des employés avec gestion des détails personnels, des contrats et des documents."
    },
    departmentManagement: {
      title: "Gestion des Départements",
      description: "Organisez la structure de votre entreprise avec des départements et des hiérarchies d'équipe."
    },
    attendanceTracking: {
      title: "Suivi des Présences",
      description: "Surveillez la présence des employés, les retards et les absences avec des rapports détaillés."
    },
    payrollManagement: {
      title: "Gestion de la Paie",
      description: "Automatisez les calculs de salaire, les déductions et générez des bulletins de paie."
    },
    leaveManagement: {
      title: "Gestion des Congés",
      description: "Gérez les demandes de congé, les congés maladie et autres absences avec des flux de travail d'approbation."
    },
    expenseManagement: {
      title: "Gestion des Dépenses",
      description: "Suivez et gérez les dépenses des employés avec des flux de travail de remboursement."
    }
  },
  techStack: {
    title: "Technologie Stack",
    backend: {
      title: "Backend",
      php: "PHP 8.1+ pour des opérations backend robustes",
      laravel: "Framework Laravel avec architecture API RESTful",
      security: "Authentification et autorisation basées sur les rôles"
    },
    frontend: {
      title: "Frontend",
      framework: "React.js avec composants UI modernes",
      typescript: "TypeScript pour la sécurité des types",
      responsive: "Système de design entièrement responsive"
    },
    integrations: {
      title: "Intégrations",
      payroll: "Intégrations de services de paie",
      calendar: "Synchronisation du système de calendrier",
      analytics: "Outils d'analyse et de reporting RH"
    }
  },
  dashboard: {
    title: "Tableau de Bord RH Puissant",
    description: "Contrôle total de vos opérations de ressources humaines avec notre panneau d'administration complet.",
    demoAlt: "Aperçu du tableau de bord administrateur de la plateforme de gestion des ressources humaines",
    features: {
      "1": "Gestion complète des données des employés",
      "2": "Suivi des présences en temps réel",
      "3": "Traitement et reporting de la paie",
      "4": "Permissions d'accès personnalisables",
      "5": "Analytique et insights sur la main-d'œuvre"
    }
  },
  cta: {
    title: "Prêt à Rationaliser Vos Processus RH?",
    description: "Découvrez comment notre Système de Gestion des Ressources Humaines peut transformer vos opérations commerciales.",
    buttonText: "Voir la Démo",
    buttonAriaLabel: "Voir la démo de la plateforme de gestion des ressources humaines"
  }
};

export const nlHRManagement = {
  hero: {
    title: "Web HR Management Systeem voor MKB",
    subtitle: "Een uitgebreide webapplicatie voor personeelsbeheer in kleine en middelgrote ondernemingen.",
    imageAlt: "HR management dashboard toont medewerkersoverzicht"
  },
  features: {
    employeeManagement: {
      title: "Medewerkersbeheer",
      description: "Volledige personeelsdossiers met persoonlijke gegevens, contracten en documentbeheer."
    },
    departmentManagement: {
      title: "Afdelingsbeheer",
      description: "Organiseer uw bedrijfsstructuur met afdelingen en teamhiërarchieën."
    },
    attendanceTracking: {
      title: "Aanwezigheidsregistratie",
      description: "Monitor aanwezigheid, te laat komen en afwezigheid van medewerkers met gedetailleerde rapporten."
    },
    payrollManagement: {
      title: "Salarisadministratie",
      description: "Automatiseer salarisberekeningen, inhoudingen en genereer loonstroken."
    },
    leaveManagement: {
      title: "Verlofbeheer",
      description: "Beheer vakantieaanvragen, ziekteverlof en andere afwezigheid met goedkeuringsworkflows."
    },
    expenseManagement: {
      title: "Onkostenbeheer",
      description: "Volg en beheer personeelskosten met terugbetalingsworkflows."
    }
  },
  techStack: {
    title: "Technologiestack",
    backend: {
      title: "Backend",
      php: "PHP 8.1+ voor robuuste backend-operaties",
      laravel: "Laravel framework met RESTful API-architectuur",
      security: "Rolgebaseerde authenticatie en autorisatie"
    },
    frontend: {
      title: "Frontend",
      framework: "React.js met moderne UI-componenten",
      typescript: "TypeScript voor typeveiligheid",
      responsive: "Volledig responsief designsysteem"
    },
    integrations: {
      title: "Integraties",
      payroll: "Integraties met salarisdiensten",
      calendar: "Synchronisatie met kalendersystemen",
      analytics: "HR analytics en rapportagetools"
    }
  },
  dashboard: {
    title: "Krachtig HR Dashboard",
    description: "Volledige controle over uw HR-operaties met ons uitgebreide adminpanel.",
    demoAlt: "Preview van HR management admin dashboard",
    features: {
      "1": "Uitgebreid medewerkersdatabeheer",
      "2": "Realtime aanwezigheidsmonitoring",
      "3": "Salarisverwerking en rapportage",
      "4": "Aanpasbare toegangsrechten",
      "5": "Workforce analytics en inzichten"
    }
  },
  cta: {
    title: "Klaar om uw HR-processen te stroomlijnen?",
    description: "Ontdek hoe ons HR Management Systeem uw bedrijfsvoering kan transformeren.",
    buttonText: "Bekijk Demo",
    buttonAriaLabel: "Bekijk demo van het HR management platform"
  }
};

export const deHRManagement = {
  hero: {
    title: "Web HR Management System für KMU",
    subtitle: "Eine umfassende Webanwendung für das Personalmanagement in kleinen und mittleren Unternehmen.",
    imageAlt: "HR-Management-Dashboard zeigt Mitarbeiterübersicht"
  },
  features: {
    employeeManagement: {
      title: "Mitarbeiterverwaltung",
      description: "Vollständige Mitarbeiterakten mit persönlichen Daten, Verträgen und Dokumentenverwaltung."
    },
    departmentManagement: {
      title: "Abteilungsverwaltung",
      description: "Organisieren Sie Ihre Unternehmensstruktur mit Abteilungen und Teamhierarchien."
    },
    attendanceTracking: {
      title: "Anwesenheitsverfolgung",
      description: "Überwachen Sie Anwesenheit, Verspätungen und Abwesenheiten mit detaillierten Berichten."
    },
    payrollManagement: {
      title: "Lohn- und Gehaltsabrechnung",
      description: "Automatisieren Sie Gehaltsberechnungen, Abzüge und erstellen Sie Gehaltsabrechnungen."
    },
    leaveManagement: {
      title: "Urlaubsverwaltung",
      description: "Verwalten Sie Urlaubsanträge, Krankmeldungen und andere Abwesenheiten mit Genehmigungsworkflows."
    },
    expenseManagement: {
      title: "Spesenmanagement",
      description: "Verfolgen und verwalten Sie Mitarbeiterausgaben mit Erstattungsworkflows."
    }
  },
  techStack: {
    title: "Technologie-Stack",
    backend: {
      title: "Backend",
      php: "PHP 8.1+ für robuste Backend-Operationen",
      laravel: "Laravel Framework mit RESTful API-Architektur",
      security: "Rollenbasierte Authentifizierung und Autorisierung"
    },
    frontend: {
      title: "Frontend",
      framework: "React.js mit modernen UI-Komponenten",
      typescript: "TypeScript für Typsicherheit",
      responsive: "Vollständig responsives Designsystem"
    },
    integrations: {
      title: "Integrationen",
      payroll: "Lohnabrechnungsdienst-Integrationen",
      calendar: "Kalendersystem-Synchronisierung",
      analytics: "HR-Analyse- und Reporting-Tools"
    }
  },
  dashboard: {
    title: "Leistungsstarkes HR-Dashboard",
    description: "Vollständige Kontrolle über Ihre HR-Operationen mit unserem umfassenden Admin-Panel.",
    demoAlt: "Vorschau des HR-Management-Admin-Dashboards",
    features: {
      "1": "Umfassende Mitarbeiterdatenverwaltung",
      "2": "Echtzeit-Anwesenheitsüberwachung",
      "3": "Lohnabrechnung und Reporting",
      "4": "Anpassbare Zugriffsberechtigungen",
      "5": "Workforce-Analysen und Einblicke"
    }
  },
  cta: {
    title: "Bereit, Ihre HR-Prozesse zu optimieren?",
    description: "Erfahren Sie, wie unser HR-Management-System Ihre Geschäftsabläufe transformieren kann.",
    buttonText: "Demo ansehen",
    buttonAriaLabel: "Demo der HR-Management-Plattform ansehen"
  }
};